// Customer Brain Panel — Fase 5 · Cérebro do Cliente (Fev/2026)
//
// Painel visual "Apple premium" dentro do modal de edição do cliente.
// Consome os endpoints da Fase 3/4:
//   • GET  /api/customer-brain/{customer_id}/stats
//   • POST /api/customer-brain/{customer_id}/search  { query, top_k }
//
// Uso:
//   <CustomerBrainPanel customerId={customer.id} customerName={customer.name} />
//
// Fluxo UX:
//   1. Header monocromático com badge "🧠 47 fragmentos · 12 documentos"
//   2. Search bar com debounce (350ms) — só busca se ≥ 3 chars
//   3. Empty state amigável enquanto sem query
//   4. Resultados como cards limpos, cada um com:
//      • Snippet do chunk (destaque em texto)
//      • Fonte (PDF/áudio/anotação) + score BM25
//      • Link "abrir documento" (chama onOpenDocument prop se fornecido)
//
// Design decisions:
//   • NÃO chama /search em cada tecla — usa debounce cirúrgico
//   • min_score=0.5 (mesmo cutoff do backend) — evita cards de baixa relevância
//   • Feedback de loading só quando > 250ms (evita flicker)
//   • data-testid em TODOS os elementos interativos (Marcelo cobra)

import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Brain, MagnifyingGlass, FilePdf, Microphone, NotePencil, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const DEBOUNCE_MS = 350;
const MIN_QUERY_CHARS = 3;
const DEFAULT_TOP_K = 5;

// Rótulo + ícone por tipo de fonte (bate com backend `source_kind`)
const SOURCE_META = {
  pdf_text:         { label: "PDF",       icon: FilePdf,     className: "bg-rose-50 text-rose-700 border-rose-200" },
  audio_transcript: { label: "Áudio",     icon: Microphone,  className: "bg-violet-50 text-violet-700 border-violet-200" },
  manual:           { label: "Anotação",  icon: NotePencil,  className: "bg-slate-100 text-slate-700 border-slate-200" },
};

function SourceChip({ kind }) {
  const meta = SOURCE_META[kind] || SOURCE_META.manual;
  const Icon = meta.icon;
  return (
    <span
      data-testid={`brain-hit-source-${kind}`}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${meta.className}`}
    >
      <Icon size={9} weight="fill" />
      {meta.label}
    </span>
  );
}

function ScoreBadge({ score }) {
  // score BM25 típico entre 0.5 e 4 em nosso corpus. Normaliza pra 0-100
  // apenas pra UX (nunca exposto como "certeza absoluta" — evita
  // criar expectativa errada no operador).
  const pct = Math.min(100, Math.round((score || 0) * 25));
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-100" title={`Score BM25: ${score}`}>
      <Sparkle size={9} weight="fill" />
      {pct}%
    </span>
  );
}

export function CustomerBrainPanel({ customerId, customerName, onOpenDocument }) {
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState([]);
  const [stats, setStats] = useState({ chunks: 0, documents_indexed: 0 });
  const [loading, setLoading] = useState(false);
  const [statsLoaded, setStatsLoaded] = useState(false);
  const debounceRef = useRef(null);

  // Stats: carrega 1x
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/customer-brain/${customerId}/stats`);
        if (!cancelled) setStats(data || { chunks: 0, documents_indexed: 0 });
      } catch {
        // Silencioso — endpoint pode não existir em tenants legados
      } finally {
        if (!cancelled) setStatsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  // Search com debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < MIN_QUERY_CHARS) {
      setHits([]);
      setLoading(false);
      return;
    }
    // Feedback de loading só após 250ms — evita flicker em queries rápidas
    const loadingTimer = setTimeout(() => setLoading(true), 250);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post(`/customer-brain/${customerId}/search`, {
          query: q,
          top_k: DEFAULT_TOP_K,
          min_score: 0.5,
        });
        setHits(Array.isArray(data?.hits) ? data.hits : []);
      } catch (err) {
        // Erros de rede não são "vazios" — mostra toast
        const msg = err?.response?.data?.detail || "Falha ao buscar no Cérebro.";
        toast.error(msg);
        setHits([]);
      } finally {
        clearTimeout(loadingTimer);
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(loadingTimer);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, customerId]);

  const hasBrain = stats.chunks > 0;
  const displayName = (customerName || "este cliente").split(" ")[0];

  const highlightedHits = useMemo(() => {
    // Highlight leve dos termos da query no snippet — apenas visual,
    // não altera nada do BM25.
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return hits.map((h) => {
      const text = h.text || "";
      if (!terms.length) return { ...h, __html: text };
      // Regex simples que ignora acentos: mantém match visual sem
      // introduzir dependência de biblioteca no cliente.
      let out = text;
      const seen = new Set();
      for (const t of terms) {
        if (t.length < 2 || seen.has(t)) continue;
        seen.add(t);
        const rx = new RegExp(`(${t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
        out = out.replace(rx, "<mark class=\"bg-amber-100 text-amber-900 rounded px-0.5\">$1</mark>");
      }
      return { ...h, __html: out };
    });
  }, [hits, query]);

  return (
    <section
      className="mt-6 pt-5 border-t border-slate-100"
      data-testid="customer-brain-panel"
    >
      {/* Header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-900 text-white">
            <Brain size={16} weight="fill" />
          </span>
          <div>
            <div className="text-[13px] font-semibold text-slate-900 flex items-center gap-1.5">
              Cérebro do Cliente
              {hasBrain && (
                <span
                  data-testid="customer-brain-stats-badge"
                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100"
                >
                  {stats.chunks} fragmento{stats.chunks !== 1 ? "s" : ""} · {stats.documents_indexed} doc{stats.documents_indexed !== 1 ? "s" : ""}
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 leading-tight">
              Busque no histórico do {displayName} — o AgentUS aprende com PDFs e áudios enviados.
            </div>
          </div>
        </div>
      </div>

      {/* Search bar ─────────────────────────────────────────── */}
      <div className="relative">
        <MagnifyingGlass
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={
            hasBrain
              ? `Ex: "qual o valor do último orçamento?"`
              : "O Cérebro ainda está vazio…"
          }
          disabled={!hasBrain && statsLoaded}
          data-testid="customer-brain-search-input"
          className="w-full pl-9 pr-3 py-2.5 text-[13px] rounded-xl border border-slate-200 bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 disabled:bg-slate-50 disabled:cursor-not-allowed"
        />
      </div>

      {/* Corpo ──────────────────────────────────────────────── */}
      <div className="mt-3">
        {!statsLoaded ? (
          <div className="text-[12px] text-slate-400 py-3" data-testid="customer-brain-loading-stats">
            Carregando Cérebro…
          </div>
        ) : !hasBrain ? (
          <div
            className="text-[12.5px] text-slate-500 italic bg-slate-50 border border-slate-100 rounded-lg p-3"
            data-testid="customer-brain-empty"
          >
            O Cérebro ainda não tem conteúdo. Quando o {displayName} enviar PDFs ou áudios pelo WhatsApp
            (ou você anexar documentos), o texto é indexado automaticamente e passa a ajudar o AgentUS a responder.
          </div>
        ) : query.trim().length < MIN_QUERY_CHARS ? (
          <div
            className="text-[12.5px] text-slate-500 italic bg-slate-50 border border-slate-100 rounded-lg p-3"
            data-testid="customer-brain-idle"
          >
            Digite ao menos {MIN_QUERY_CHARS} caracteres pra buscar. Exemplo:
            {" \"valor do orçamento\", \"data da última visita\", \"contrato assinado\"."}
          </div>
        ) : loading ? (
          <div className="text-[12.5px] text-slate-400 py-3" data-testid="customer-brain-loading">
            Buscando no Cérebro…
          </div>
        ) : hits.length === 0 ? (
          <div
            className="text-[12.5px] text-slate-500 italic bg-slate-50 border border-slate-100 rounded-lg p-3"
            data-testid="customer-brain-no-hits"
          >
            Nada relevante encontrado pra <b>&ldquo;{query}&rdquo;</b>. Tente termos mais específicos
            ou revise se os documentos foram anexados a este cliente.
          </div>
        ) : (
          <ul className="space-y-2" data-testid="customer-brain-hits">
            {highlightedHits.map((h) => (
              <li
                key={h.chunk_id}
                data-testid={`customer-brain-hit-${h.chunk_id}`}
                className="rounded-lg border border-slate-200 bg-white p-3 hover:border-slate-300 transition-colors"
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <SourceChip kind={h.source_kind} />
                    <ScoreBadge score={h.score} />
                    <span className="text-[10px] text-slate-400 font-mono">
                      #{h.chunk_index}
                    </span>
                  </div>
                  {onOpenDocument && (
                    <button
                      type="button"
                      onClick={() => onOpenDocument(h.document_id)}
                      data-testid={`customer-brain-open-doc-${h.document_id}`}
                      className="text-[11px] font-medium text-slate-500 hover:text-slate-900"
                    >
                      abrir documento →
                    </button>
                  )}
                </div>
                <div
                  className="text-[12.5px] text-slate-700 leading-snug"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: h.__html }}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export default CustomerBrainPanel;
